import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ScoreCard from '../components/ScoreCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import { HardDrive, Plus, Loader2, Download, Copy, CheckCircle2, XCircle, Activity, Trash2, Search, Filter } from 'lucide-react';

const AssetsRMM = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('none');
  const [installScript, setInstallScript] = useState('');
  const [installCommand, setInstallCommand] = useState('');
  const [scriptCopied, setScriptCopied] = useState(false);
  const [agentType, setAgentType] = useState('gui'); // 'gui' or 'powershell'
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClientId, setFilterClientId] = useState('all');

  useEffect(() => {
    fetchData();
  }, []); // Only fetch once on mount

  const fetchData = async () => {
    setLoading(true);
    try {
      const [agentsRes, clientsRes] = await Promise.all([
        api.get('/agents'),
        api.get('/clients')
      ]);
      setAgents(agentsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInstaller = async () => {
    try {
      const endpoint = agentType === 'gui' ? '/agents/generate-gui-installer' : '/agents/generate-installer';
      const response = await api.post(endpoint, {
        client_id: selectedClientId === 'none' ? null : selectedClientId
      });
      
      setInstallScript(response.data.install_script);
      setInstallCommand(response.data.install_command);
      toast.success('Installation script generated!');
    } catch (error) {
      toast.error('Failed to generate installer');
    }
  };

  const copyToClipboard = (text) => {
    // Fallback method for environments where Clipboard API is blocked
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          setScriptCopied(true);
          toast.success('Copied to clipboard!');
          setTimeout(() => setScriptCopied(false), 2000);
        }).catch(() => {
          fallbackCopy(text);
        });
      } else {
        fallbackCopy(text);
      }
    } catch (error) {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    // Create temporary textarea element
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    
    try {
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      
      if (successful) {
        setScriptCopied(true);
        toast.success('Copied to clipboard!');
        setTimeout(() => setScriptCopied(false), 2000);
      } else {
        toast.error('Failed to copy. Please copy manually.');
      }
    } catch (err) {
      toast.error('Failed to copy. Please copy manually.');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleRemoteControl = async (agent) => {
    try {
      const settingsRes = await api.get('/settings');
      const screenconnectUrl = settingsRes.data.screenconnect_url;
      
      if (!screenconnectUrl) {
        toast.error('ScreenConnect URL not configured. Please check Settings.');
        return;
      }
      
      // Clean URL (remove trailing slash)
      const baseUrl = screenconnectUrl.replace(/\/$/, '');
      const sessionId = agent.screenconnect_session_id;
      
      // Build ScreenConnect URL
      const url = `${baseUrl}/Host#Access/All%20Machines%1FBitzUK//${sessionId}/Join`;
      
      // Open in popup window instead of tab
      const width = 1024;
      const height = 768;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const popup = window.open(
        url,
        `ScreenConnect_${agent.hostname}`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=yes,resizable=yes,scrollbars=yes`
      );
      
      if (popup) {
        popup.focus();
        toast.success(`Opening remote session for ${agent.hostname}...`);
      } else {
        toast.error('Popup blocked! Please allow popups for this site.');
      }
    } catch (error) {
      toast.error('Failed to load ScreenConnect settings');
    }
  };

  const handleDeleteAgent = async (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    const isOnline = agent?.status === 'online';
    
    if (!confirm(`Delete this agent?${isOnline ? '\n\nThe agent is online and will be automatically uninstalled from the remote computer.' : ''}`)) return;
    
    try {
      if (isOnline) {
        toast.info('Sending uninstall command to agent...');
      }
      const response = await api.delete(`/agents/${agentId}`);
      
      if (response.data.uninstall_sent) {
        toast.success('Agent deleted and uninstall command sent!');
      } else {
        toast.success('Agent deleted from portal!');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.company_name : 'No Client';
  };

  const getStatusIcon = (status) => {
    if (status === 'online') return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    if (status === 'offline') return <XCircle className="w-5 h-5 text-red-600" />;
    return <Activity className="w-5 h-5 text-yellow-600" />;
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Never';
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const formatUptime = (seconds) => {
    if (!seconds) return 'Unknown';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Filter and search agents
  const filteredAgents = agents.filter(agent => {
    // Filter by client
    const clientMatch = filterClientId === 'all' || 
                       (filterClientId === 'none' && !agent.client_id) ||
                       agent.client_id === filterClientId;
    
    if (!clientMatch) return false;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const hostnameMatch = agent.hostname?.toLowerCase().includes(query);
      const friendlyNameMatch = agent.friendly_name?.toLowerCase().includes(query);
      const ipMatch = agent.ip_address?.toLowerCase().includes(query);
      const cpuMatch = agent.cpu_info?.toLowerCase().includes(query);
      const clientName = getClientName(agent.client_id).toLowerCase();
      const clientMatch = clientName.includes(query);
      const currentUser = agent.system_details?.current_user;
      const userMatch = currentUser ? 
        `${currentUser.domain}\\${currentUser.username}`.toLowerCase().includes(query) : 
        false;
      
      return hostnameMatch || friendlyNameMatch || ipMatch || cpuMatch || clientMatch || userMatch;
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">RMM Assets</h1>
            <p className="text-slate-600">Loading agents...</p>
          </div>
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800">RMM Assets</h1>
              <p className="text-sm md:text-base text-slate-600 mt-1">Remote monitoring & management agents</p>
            </div>
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-md w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Asset
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-slate-600">Total Agents</p>
                    <p className="text-xl md:text-2xl font-bold text-slate-800">{agents.length}</p>
                  </div>
                  <HardDrive className="w-6 h-6 md:w-8 md:h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-slate-600">Online</p>
                    <p className="text-xl md:text-2xl font-bold text-green-600">
                      {agents.filter(a => a.status === 'online').length}
                    </p>
                  </div>
                  <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-slate-600">Offline</p>
                    <p className="text-xl md:text-2xl font-bold text-red-600">
                      {agents.filter(a => a.status === 'offline').length}
                    </p>
                  </div>
                  <XCircle className="w-6 h-6 md:w-8 md:h-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-slate-600">Pending</p>
                    <p className="text-xl md:text-2xl font-bold text-yellow-600">
                      {agents.filter(a => a.status === 'pending').length}
                    </p>
                  </div>
                  <Activity className="w-6 h-6 md:w-8 md:h-8 text-yellow-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Search and Filter Bar */}
        {agents.length > 0 && (
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by hostname, friendly name, IP, client, user, CPU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-slate-300"
              />
            </div>

            {/* Client Filter Dropdown */}
            <div className="w-full sm:w-64">
              <Select value={filterClientId} onValueChange={setFilterClientId}>
                <SelectTrigger className="border-slate-300">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-500" />
                    <SelectValue placeholder="All Clients" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  <SelectItem value="none">No Client Assigned</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters Button */}
            {(searchQuery || filterClientId !== 'all') && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setFilterClientId('all');
                }}
                className="whitespace-nowrap"
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}

        {/* Agents List */}
        {agents.length === 0 ? (
          <Card>
            <CardContent className="py-20 text-center">
              <HardDrive className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No agents yet</h3>
              <p className="text-slate-500 mb-6">Deploy your first RMM agent</p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Results count */}
            {(searchQuery || filterClientId !== 'all') && (
              <div className="mb-4 text-sm text-slate-600">
                Showing <span className="font-semibold">{filteredAgents.length}</span> of <span className="font-semibold">{agents.length}</span> agents
              </div>
            )}

            {/* No results message */}
            {filteredAgents.length === 0 ? (
              <Card>
                <CardContent className="py-20 text-center">
                  <Search className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No agents found</h3>
                  <p className="text-slate-500 mb-6">Try adjusting your search or filters</p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setFilterClientId('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredAgents.map((agent) => (
              <Card key={agent.id} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex items-start space-x-3 md:space-x-4 flex-1">
                      <div className="mt-1 flex-shrink-0">{getStatusIcon(agent.status)}</div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <div className="min-w-0">
                            <h3
                              className="text-base md:text-lg font-semibold text-slate-800 hover:text-blue-600 cursor-pointer transition-colors truncate"
                              onClick={() => navigate(`/assets/${agent.id}`)}
                            >
                              {agent.hostname}
                            </h3>
                            {agent.friendly_name && (
                              <p className="text-xs md:text-sm text-slate-500 mt-0.5 truncate">{agent.friendly_name}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="uppercase text-xs flex-shrink-0">v{agent.version}</Badge>
                          {agent.client_id && (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs flex-shrink-0">
                              {getClientName(agent.client_id)}
                            </Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">CPU</p>
                            <p className="font-semibold text-slate-700">{agent.cpu_info || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Memory</p>
                            <p className="font-semibold text-slate-700">
                              {agent.total_memory_gb ? `${agent.total_memory_gb} GB` : 'Unknown'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">IP Address</p>
                            <p className="font-semibold text-slate-700">{agent.ip_address || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Current User</p>
                            <p className="font-semibold text-slate-700">
                              {agent.system_details?.current_user ? 
                                `${agent.system_details.current_user.domain}\\${agent.system_details.current_user.username}` : 
                                'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Uptime</p>
                            <p className="font-semibold text-slate-700">
                              {agent.system_details?.uptime_seconds ? 
                                formatUptime(agent.system_details.uptime_seconds) : 
                                'Unknown'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Last Seen</p>
                            <p className="font-semibold text-slate-700">{formatLastSeen(agent.last_seen)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-row lg:flex-col justify-between lg:justify-start items-center lg:items-end space-x-2 lg:space-x-0 lg:space-y-2 lg:ml-4">
                      {/* Health Scores - Horizontal on mobile, vertical on desktop */}
                      <div className="flex space-x-2">
                        <div onClick={() => navigate(`/assets/${agent.id}`)} className="cursor-pointer">
                          <ScoreCard
                            type="security"
                            score={agent.security_score ?? null}
                            color={agent.security_score ? (
                              agent.security_score >= 80 ? 'green' : 
                              agent.security_score >= 60 ? 'amber' : 'red'
                            ) : 'gray'}
                            size="small"
                          />
                        </div>
                        <div onClick={() => navigate(`/assets/${agent.id}`)} className="cursor-pointer">
                          <ScoreCard
                            type="performance"
                            score={agent.performance_score ?? null}
                            color={agent.performance_score ? (
                              agent.performance_score >= 80 ? 'green' : 
                              agent.performance_score >= 60 ? 'amber' : 'red'
                            ) : 'gray'}
                            size="small"
                          />
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant={agent.screenconnect_session_id ? "default" : "outline"}
                          onClick={() => agent.screenconnect_session_id && handleRemoteControl(agent)}
                          disabled={!agent.screenconnect_session_id}
                          className={agent.screenconnect_session_id ? 
                            "bg-green-600 hover:bg-green-700 text-white" : 
                            "border-slate-300 text-slate-400 cursor-not-allowed"}
                          title={agent.screenconnect_session_id ? 
                            "Remote Control via ScreenConnect" : 
                            "ScreenConnect not detected on this agent"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteAgent(agent.id)}
                          className="border-red-200 text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Asset Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Add New Asset</DialogTitle>
            <DialogDescription>
              Deploy RMM agent to a Windows computer
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!installScript ? (
              <>
                <div className="space-y-2">
                  <Label>Agent Type</Label>
                  <Select value={agentType} onValueChange={setAgentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gui">GUI Agent (.NET) - Recommended</SelectItem>
                      <SelectItem value="powershell">PowerShell Agent (Legacy)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {agentType === 'gui' 
                      ? 'Professional Windows application with system tray icon and status window'
                      : 'Lightweight PowerShell-based agent running as scheduled task'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Client (Optional)</Label>
                  <Select value={selectedClientId || "none"} onValueChange={(val) => setSelectedClientId(val === "none" ? "" : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Client</SelectItem>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={handleGenerateInstaller} className="w-full bg-purple-600 hover:bg-purple-700">
                  <Download className="w-4 h-4 mr-2" />
                  Generate Installation Script
                </Button>

                <div className="border-t pt-4 mt-4">
                  <Label className="text-sm text-slate-600 mb-2 block">Or Download Agent Directly</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/agent-download?arch=x64`, '_blank')}
                      className="w-full"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download x64
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/agent-download?arch=x86`, '_blank')}
                      className="w-full"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download x86
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Download the agent installer for manual installation or deployment via Group Policy
                  </p>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Installation Instructions</h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Open PowerShell as Administrator on the target computer</li>
                    <li>Copy and run the command below</li>
                    <li>Agent will appear in the portal within 60 seconds</li>
                    {agentType === 'gui' && <li className="font-semibold">A system tray icon will appear once installed</li>}
                  </ol>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Quick Install Command (Recommended)</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(installCommand)}
                    >
                      {scriptCopied ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {scriptCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <Textarea
                    value={installCommand}
                    readOnly
                    rows={2}
                    className="font-mono text-xs bg-slate-50"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Full Installation Script</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(installScript)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                  <Textarea
                    value={installScript}
                    readOnly
                    rows={12}
                    className="font-mono text-xs bg-slate-50"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAddDialog(false);
                setInstallScript('');
                setInstallCommand('');
                setSelectedClientId('none');
                setScriptCopied(false);
                setAgentType('gui');
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetsRMM;
